import React, { useState } from 'react';
import { Loader2, RefreshCw, Power, PowerOff, Copy, Edit, Plus, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/lib/apiClient';
import { copyToClipboard } from '@/lib/dashboardUtils';

export default function GemeindenTab({ isDark, municipalitiesList, loadingMunicipalities, municipalityWidgets, fetchMunicipalities }) {
  const [originsEditOpen, setOriginsEditOpen] = useState(false);
  const [editingMunicipality, setEditingMunicipality] = useState(null);
  const [editingOrigins, setEditingOrigins] = useState([]);
  const [newOriginInput, setNewOriginInput] = useState('');
  const [savingOrigins, setSavingOrigins] = useState(false);
  const [editingLlmModel, setEditingLlmModel] = useState('gpt-4o');

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', slug: '', origins: [] });
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [createOriginInput, setCreateOriginInput] = useState('');
  const [creatingMunicipality, setCreatingMunicipality] = useState(false);
  const [createResult, setCreateResult] = useState(null);

  const deriveSlug = (name) =>
    name.toLowerCase().replace(/[\s_]+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-').replace(/^-|-$/g, '');

  const handleCreateNameChange = (name) => {
    setCreateForm(prev => ({
      ...prev,
      name,
      ...(slugManuallyEdited ? {} : { slug: deriveSlug(name) }),
    }));
  };

  const handleCreateSlugChange = (slug) => {
    setSlugManuallyEdited(true);
    setCreateForm(prev => ({ ...prev, slug: slug.toLowerCase().replace(/[^a-z0-9-]/g, '') }));
  };

  const normalizeOrigin = (raw) => {
    let normalized = raw.trim();
    if (!normalized) return null;
    if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
      try { normalized = new URL(normalized).hostname; }
      catch { normalized = normalized.replace(/^https?:\/\//, '').split('/')[0]; }
    } else {
      normalized = normalized.split('/')[0];
    }
    return normalized.split(':')[0];
  };

  const handleCreateAddOrigin = () => {
    const normalized = normalizeOrigin(createOriginInput);
    if (!normalized) { toast.error('Bitte geben Sie eine Domain ein'); return; }
    if (createForm.origins.includes(normalized)) { toast.error('Diese Domain ist bereits vorhanden'); return; }
    setCreateForm(prev => ({ ...prev, origins: [...prev.origins, normalized] }));
    setCreateOriginInput('');
  };

  const handleCreateRemoveOrigin = (origin) => {
    setCreateForm(prev => ({ ...prev, origins: prev.origins.filter(o => o !== origin) }));
  };

  const handleResetCreateDialog = () => {
    setCreateDialogOpen(false);
    setCreateForm({ name: '', slug: '', origins: [] });
    setSlugManuallyEdited(false);
    setCreateOriginInput('');
    setCreatingMunicipality(false);
    setCreateResult(null);
  };

  const handleCreateMunicipality = async () => {
    if (!createForm.name.trim()) { toast.error('Bitte geben Sie einen Namen ein'); return; }
    if (!createForm.slug.trim()) { toast.error('Bitte geben Sie einen Slug ein'); return; }
    if (createForm.origins.length === 0) { toast.error('Mindestens eine Domain muss angegeben werden'); return; }
    setCreatingMunicipality(true);
    try {
      const response = await apiClient.post('/provider/municipalities', {
        name: createForm.name.trim(),
        slug: createForm.slug.trim(),
        allowed_embed_origins: createForm.origins,
      });
      toast.success('Gemeinde erfolgreich erstellt');
      setCreateResult(response.data);
      fetchMunicipalities();
    } catch (error) {
      console.error('Error creating municipality:', error);
      const detail = error.response?.data?.detail || error.response?.data?.message || error.message || 'Unbekannter Fehler';
      if (error.response?.status === 409) {
        toast.error(`Slug "${createForm.slug}" ist bereits vergeben`);
      } else {
        toast.error(`Fehler beim Erstellen: ${detail}`);
      }
    } finally {
      setCreatingMunicipality(false);
    }
  };

  const handleActivateMunicipality = async (municipalityId) => {
    if (!window.confirm('Möchten Sie diese Gemeinde wirklich aktivieren? (Status: Entwurf → Aktiv)')) return;
    try {
      await apiClient.post(`/provider/municipalities/${municipalityId}/activate?allowWithoutAcceptedInvite=true`);
      toast.success('Gemeinde erfolgreich aktiviert');
      fetchMunicipalities();
    } catch (error) {
      console.error('Error activating municipality:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Fehler beim Aktivieren';
      toast.error(`Fehler beim Aktivieren: ${errorMessage}`);
    }
  };

  const handleToggleWidget = async (widgetId, isActive) => {
    const action = isActive ? 'pausieren' : 'aktivieren';
    if (!window.confirm(`Möchten Sie dieses Widget wirklich ${action}?`)) return;
    try {
      const endpoint = isActive ? `/provider/widgets/${widgetId}/pause` : `/provider/widgets/${widgetId}/resume`;
      await apiClient.post(endpoint);
      toast.success(`Widget erfolgreich ${isActive ? 'pausiert' : 'aktiviert'}`);
      fetchMunicipalities();
    } catch (error) {
      console.error(`Error ${isActive ? 'pausing' : 'resuming'} widget:`, error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || `Fehler beim ${action}`;
      toast.error(`Fehler beim ${action}: ${errorMessage}`);
    }
  };

  const handleOpenOriginsEdit = (municipality) => {
    setEditingMunicipality(municipality);
    setEditingOrigins([...(municipality.allowed_embed_origins || [])]);
    setNewOriginInput('');
    const currentLlmModel = municipality?.rack_config?.llm_model || 'gpt-4o';
    setEditingLlmModel(currentLlmModel);
    setOriginsEditOpen(true);
  };

  const handleCloseOriginsEdit = () => {
    setOriginsEditOpen(false);
    setEditingMunicipality(null);
    setEditingOrigins([]);
    setNewOriginInput('');
    setEditingLlmModel('gpt-4o');
  };

  const handleAddOrigin = () => {
    const origin = newOriginInput.trim();
    if (!origin) { toast.error('Bitte geben Sie eine Domain ein'); return; }
    let normalized = origin;
    if (origin.startsWith('http://') || origin.startsWith('https://')) {
      try { const url = new URL(origin); normalized = url.hostname; }
      catch (e) { normalized = origin.replace(/^https?:\/\//, '').split('/')[0]; }
    } else {
      normalized = origin.split('/')[0];
    }
    normalized = normalized.split(':')[0];
    if (editingOrigins.includes(normalized)) { toast.error('Diese Domain ist bereits vorhanden'); return; }
    setEditingOrigins([...editingOrigins, normalized]);
    setNewOriginInput('');
  };

  const handleRemoveOrigin = (originToRemove) => {
    if (editingOrigins.length <= 1) { toast.error('Mindestens eine Domain muss vorhanden sein'); return; }
    setEditingOrigins(editingOrigins.filter(o => o !== originToRemove));
  };

  const handleSaveOrigins = async () => {
    if (editingOrigins.length === 0) { toast.error('Mindestens eine Domain muss vorhanden sein'); return; }
    setSavingOrigins(true);
    try {
      const updatePayload = { allowed_embed_origins: editingOrigins };
      if (editingLlmModel) { updatePayload.rack_config = { llm_model: editingLlmModel }; }
      await apiClient.patch(`/provider/municipalities/${editingMunicipality.id}`, updatePayload);
      toast.success('Einstellungen erfolgreich aktualisiert');
      handleCloseOriginsEdit();
      fetchMunicipalities();
    } catch (error) {
      console.error('Error saving settings:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Fehler beim Speichern';
      toast.error(`Fehler beim Speichern: ${errorMessage}`);
    } finally {
      setSavingOrigins(false);
    }
  };

  return (
    <>
      <div className="flex justify-between items-center">
        <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Gemeinden & Widgets</h2>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setCreateDialogOpen(true)}
            className="bg-sky-600 hover:bg-sky-500 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neue Gemeinde anlegen
          </Button>
          <Button 
            variant="outline" 
            onClick={fetchMunicipalities}
            disabled={loadingMunicipalities}
            className={isDark 
              ? "border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800 hover:text-white" 
              : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
            }
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loadingMunicipalities ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>
      </div>

      <Card className={isDark 
        ? "bg-slate-900 border-slate-800 shadow-lg" 
        : "bg-slate-50 border-slate-200 shadow-lg"
      }>
        <CardContent className="p-0">
          <Table>
            <TableHeader className={isDark ? "bg-slate-950" : "bg-slate-50"}>
              <TableRow className={isDark ? "border-slate-800 hover:bg-slate-950" : "border-slate-200 hover:bg-slate-50"}>
                <TableHead className={isDark ? "text-slate-200" : "text-slate-600"}>Gemeinde</TableHead>
                <TableHead className={isDark ? "text-slate-200" : "text-slate-600"}>Slug</TableHead>
                <TableHead className={isDark ? "text-slate-200" : "text-slate-600"}>Status</TableHead>
                <TableHead className={isDark ? "text-slate-200" : "text-slate-600"}>Widget-ID</TableHead>
                <TableHead className={isDark ? "text-slate-200" : "text-slate-600"}>Widget Status</TableHead>
                <TableHead className={isDark ? "text-slate-200" : "text-slate-600"}>Origins</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loadingMunicipalities ? (
                <TableRow>
                  <TableCell colSpan={6} className={`h-32 text-center ${isDark ? "text-slate-500" : "text-slate-600"}`}>
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Lade Gemeinden...
                  </TableCell>
                </TableRow>
              ) : municipalitiesList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className={`h-32 text-center ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                    Keine Gemeinden gefunden.
                  </TableCell>
                </TableRow>
              ) : (
                municipalitiesList.map((muni) => {
                  const widgets = municipalityWidgets[muni.id] || [];
                  const activeWidget = widgets.find(w => w.is_active);
                  return (
                    <TableRow key={muni.id} className={isDark 
                      ? "border-slate-800 hover:bg-slate-800/50 transition-colors" 
                      : "border-slate-200 hover:bg-slate-50 transition-colors"
                    }>
                      <TableCell className={`font-medium ${isDark ? "text-slate-100" : "text-slate-900"}`}>
                        <div className="flex flex-col">
                          <span>{muni.name}</span>
                          <span className={`text-xs font-mono ${isDark ? "text-slate-400" : "text-slate-500"}`}>{muni.id}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={isDark ? "border-slate-500 text-slate-200" : "border-slate-400 text-slate-700"}>
                          {muni.slug || '—'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge className={muni.status === 'ACTIVE' ? "bg-green-500/15 text-green-600 border-green-200" : "bg-yellow-500/15 text-yellow-600 border-yellow-200"}>
                            {muni.status === 'ACTIVE' ? 'Aktiv' : 'Entwurf'}
                          </Badge>
                          {muni.status === 'DRAFT' && (
                            <Button variant="outline" size="sm" onClick={() => handleActivateMunicipality(muni.id)}
                              className={isDark ? "hover:bg-green-900/20 text-green-400 hover:text-green-300 h-7 px-2 border-green-700" : "hover:bg-green-50 text-green-700 hover:text-green-800 h-7 px-2 border-green-400"}
                              title="Gemeinde aktivieren">
                              <Power className="w-3.5 h-3.5 mr-1" /> Aktivieren
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {activeWidget ? (
                          <div className="flex items-center gap-2">
                            <code className={`text-xs font-mono px-2 py-1 rounded ${isDark ? "text-sky-300 bg-slate-950" : "text-sky-700 bg-slate-100"}`}>
                              {activeWidget.id.substring(0, 24)}...
                            </code>
                            <Button variant="ghost" size="icon" onClick={() => copyToClipboard(activeWidget.id, 'Widget-ID')}
                              className={isDark ? "hover:bg-slate-700 text-slate-400 hover:text-white h-7 w-7" : "hover:bg-slate-100 text-slate-600 hover:text-slate-900 h-7 w-7"}
                              title="Widget-ID kopieren">
                              <Copy className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <span className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>Kein Widget</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {activeWidget ? (
                          <div className="flex items-center gap-2">
                            <Badge variant={activeWidget.is_active ? "default" : "destructive"} className={activeWidget.is_active ? "bg-green-500/10 text-green-500 hover:bg-green-500/20 border-0" : ""}>
                              {activeWidget.is_active ? 'Aktiv' : 'Pausiert'}
                            </Badge>
                            <Button variant="ghost" size="icon" onClick={() => handleToggleWidget(activeWidget.id, activeWidget.is_active)}
                              className={isDark ? "hover:bg-slate-700 text-slate-400 hover:text-white h-7 w-7" : "hover:bg-slate-100 text-slate-600 hover:text-slate-900 h-7 w-7"}
                              title={activeWidget.is_active ? 'Widget pausieren' : 'Widget aktivieren'}>
                              {activeWidget.is_active ? <PowerOff className="w-3.5 h-3.5 text-orange-400" /> : <Power className="w-3.5 h-3.5 text-green-400" />}
                            </Button>
                          </div>
                        ) : (
                          <span className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="flex flex-col gap-1 max-w-[200px]">
                            {(muni.allowed_embed_origins || []).slice(0, 2).map((origin, idx) => (
                              <Badge key={idx} variant="outline" className={`text-xs ${isDark ? "border-slate-600 text-slate-300" : "border-slate-400 text-slate-600"}`}>
                                {origin}
                              </Badge>
                            ))}
                            {(muni.allowed_embed_origins || []).length > 2 && (
                              <span className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>+{(muni.allowed_embed_origins || []).length - 2} weitere</span>
                            )}
                            {(!muni.allowed_embed_origins || muni.allowed_embed_origins.length === 0) && (
                              <span className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>Keine Domains</span>
                            )}
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => handleOpenOriginsEdit(muni)}
                            className={isDark ? "hover:bg-slate-700 text-slate-400 hover:text-white h-7 w-7" : "hover:bg-slate-100 text-slate-600 hover:text-slate-900 h-7 w-7"}
                            title="Erlaubte Domains bearbeiten">
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ORIGINS EDIT DIALOG */}
      <Dialog open={originsEditOpen} onOpenChange={setOriginsEditOpen}>
        <DialogContent className={isDark 
          ? "bg-slate-900 border-slate-700 text-white sm:max-w-[600px]" 
          : "bg-white border-slate-300 text-slate-900 sm:max-w-[600px]"
        }>
          <DialogHeader>
            <DialogTitle>Gemeinde-Einstellungen bearbeiten</DialogTitle>
            <DialogDescription className={isDark ? "text-slate-300" : "text-slate-600"}>
              {editingMunicipality?.name || 'Gemeinde'} - Bearbeiten Sie erlaubte Domains und LLM-Modell.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Aktuelle erlaubte Domains</Label>
              <div className={`p-3 rounded-lg border min-h-[100px] max-h-[200px] overflow-y-auto ${isDark ? "bg-slate-950 border-slate-700" : "bg-slate-50 border-slate-300"}`}>
                {editingOrigins.length === 0 ? (
                  <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>Keine Domains vorhanden. Bitte fügen Sie mindestens eine hinzu.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {editingOrigins.map((origin, idx) => (
                      <Badge key={idx} variant="outline" className={`flex items-center gap-1 ${isDark ? "border-slate-600 text-slate-300 bg-slate-900" : "border-slate-400 text-slate-700 bg-white"}`}>
                        {origin}
                        <button onClick={() => handleRemoveOrigin(origin)} className={`ml-1 hover:opacity-70 ${isDark ? "text-slate-400 hover:text-red-400" : "text-slate-500 hover:text-red-600"}`} disabled={editingOrigins.length <= 1} title={editingOrigins.length <= 1 ? "Mindestens eine Domain erforderlich" : "Entfernen"}>
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Neue Domain hinzufügen</Label>
              <div className="flex gap-2">
                <Input value={newOriginInput} onChange={e => setNewOriginInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddOrigin(); } }}
                  placeholder="z.B. example.com oder https://www.example.com"
                  className={isDark ? "bg-slate-950 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"} />
                <Button type="button" onClick={handleAddOrigin} disabled={!newOriginInput.trim()} className="bg-sky-600 hover:bg-sky-500 text-white">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                Sie können Domains mit oder ohne Protokoll (http://, https://) eingeben. Ports werden automatisch entfernt.
              </p>
            </div>

            <div className="space-y-2">
              <Label>LLM-Modell</Label>
              <Select value={editingLlmModel} onValueChange={setEditingLlmModel}>
                <SelectTrigger className={isDark ? "bg-slate-950 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"}>
                  <SelectValue placeholder="Modell auswählen" />
                </SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"}>
                  <SelectItem value="gpt-4o">gpt-4o (Standard)</SelectItem>
                  <SelectItem value="gpt-5.2">gpt-5.2</SelectItem>
                </SelectContent>
              </Select>
              <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                Das Modell wird für alle Chat-Anfragen dieser Gemeinde verwendet.
              </p>
            </div>

            <div className={`p-3 rounded-lg ${isDark ? "bg-blue-900/20 border border-blue-800 text-blue-300" : "bg-blue-50 border border-blue-200 text-blue-700"}`}>
              <p className="text-sm">
                <strong>Hinweis:</strong> Diese Einstellungen gelten für alle Widgets dieser Gemeinde. 
                Nur Anfragen von den hier angegebenen Domains werden akzeptiert.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={handleCloseOriginsEdit} disabled={savingOrigins}
              className={isDark ? "border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveOrigins} disabled={savingOrigins || editingOrigins.length === 0} className="bg-sky-600 hover:bg-sky-500 text-white">
              {savingOrigins ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Speichern...</>) : (<><Check className="w-4 h-4 mr-2" />Speichern</>)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* CREATE MUNICIPALITY DIALOG */}
      <Dialog open={createDialogOpen} onOpenChange={(open) => { if (!open) handleResetCreateDialog(); }}>
        <DialogContent className={isDark
          ? "bg-slate-900 border-slate-700 text-white sm:max-w-[500px]"
          : "bg-white border-slate-300 text-slate-900 sm:max-w-[500px]"
        }>
          <DialogHeader>
            <DialogTitle>{createResult ? 'Gemeinde erstellt' : 'Neue Gemeinde anlegen'}</DialogTitle>
            <DialogDescription className={isDark ? "text-slate-300" : "text-slate-600"}>
              {createResult
                ? 'Die Gemeinde wurde erfolgreich erstellt. Kopieren Sie die IDs und den Embed-Code.'
                : 'Erstellen Sie eine neue Gemeinde mit Widget und Standardkonfiguration.'}
            </DialogDescription>
          </DialogHeader>

          {createResult ? (
            <div className="space-y-4 mt-4">
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className={isDark ? "text-slate-300" : "text-slate-600"}>Municipality ID</Label>
                  <div className="flex items-center gap-2">
                    <code className={`text-xs font-mono flex-1 px-3 py-2 rounded ${isDark ? "text-sky-300 bg-slate-950 border border-slate-700" : "text-sky-700 bg-slate-100 border border-slate-300"}`}>
                      {createResult.municipality_id}
                    </code>
                    <Button variant="ghost" size="icon" onClick={() => copyToClipboard(createResult.municipality_id, 'Municipality-ID')}
                      className={isDark ? "hover:bg-slate-700 text-slate-400 hover:text-white h-8 w-8" : "hover:bg-slate-100 text-slate-600 hover:text-slate-900 h-8 w-8"}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className={isDark ? "text-slate-300" : "text-slate-600"}>Widget ID</Label>
                  <div className="flex items-center gap-2">
                    <code className={`text-xs font-mono flex-1 px-3 py-2 rounded ${isDark ? "text-sky-300 bg-slate-950 border border-slate-700" : "text-sky-700 bg-slate-100 border border-slate-300"}`}>
                      {createResult.widget_id}
                    </code>
                    <Button variant="ghost" size="icon" onClick={() => copyToClipboard(createResult.widget_id, 'Widget-ID')}
                      className={isDark ? "hover:bg-slate-700 text-slate-400 hover:text-white h-8 w-8" : "hover:bg-slate-100 text-slate-600 hover:text-slate-900 h-8 w-8"}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className={isDark ? "text-slate-300" : "text-slate-600"}>Status</Label>
                  <div>
                    <Badge className="bg-yellow-500/15 text-yellow-600 border-yellow-200">
                      {createResult.status === 'ACTIVE' ? 'Aktiv' : 'Entwurf'}
                    </Badge>
                  </div>
                </div>

                {createResult.embed_snippet && (
                  <div className="space-y-1">
                    <Label className={isDark ? "text-slate-300" : "text-slate-600"}>Embed-Code</Label>
                    <div className="flex items-start gap-2">
                      <pre className={`text-xs font-mono flex-1 px-3 py-2 rounded overflow-x-auto whitespace-pre-wrap break-all ${isDark ? "text-green-300 bg-slate-950 border border-slate-700" : "text-green-700 bg-slate-100 border border-slate-300"}`}>
                        {createResult.embed_snippet}
                      </pre>
                      <Button variant="ghost" size="icon" onClick={() => copyToClipboard(createResult.embed_snippet, 'Embed-Code')}
                        className={`shrink-0 ${isDark ? "hover:bg-slate-700 text-slate-400 hover:text-white h-8 w-8" : "hover:bg-slate-100 text-slate-600 hover:text-slate-900 h-8 w-8"}`}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className={`p-3 rounded-lg ${isDark ? "bg-blue-900/20 border border-blue-800 text-blue-300" : "bg-blue-50 border border-blue-200 text-blue-700"}`}>
                <p className="text-sm">
                  <strong>Nächste Schritte:</strong> Erstellen Sie einen MunicipalityAdmin-Benutzer für diese Gemeinde und aktivieren Sie sie anschliessend.
                </p>
              </div>

              <div className="flex justify-end pt-2">
                <Button onClick={handleResetCreateDialog} className="bg-sky-600 hover:bg-sky-500 text-white">
                  <Check className="w-4 h-4 mr-2" />
                  Schliessen
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={createForm.name}
                  onChange={e => handleCreateNameChange(e.target.value)}
                  placeholder="z.B. Gemeinde StadtKI"
                  className={isDark ? "bg-slate-950 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"}
                />
              </div>

              <div className="space-y-2">
                <Label>Slug (URL-Kennung) *</Label>
                <Input
                  value={createForm.slug}
                  onChange={e => handleCreateSlugChange(e.target.value)}
                  placeholder="z.B. stadtki"
                  className={isDark ? "bg-slate-950 border-slate-700 text-white font-mono" : "bg-white border-slate-300 text-slate-900 font-mono"}
                />
                <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  Nur Kleinbuchstaben, Zahlen und Bindestriche. Wird automatisch aus dem Namen abgeleitet.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Erlaubte Domains *</Label>
                <div className={`p-3 rounded-lg border min-h-[60px] ${isDark ? "bg-slate-950 border-slate-700" : "bg-slate-50 border-slate-300"}`}>
                  {createForm.origins.length === 0 ? (
                    <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>Noch keine Domains. Bitte fügen Sie mindestens eine hinzu.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {createForm.origins.map((origin, idx) => (
                        <Badge key={idx} variant="outline" className={`flex items-center gap-1 ${isDark ? "border-slate-600 text-slate-300 bg-slate-900" : "border-slate-400 text-slate-700 bg-white"}`}>
                          {origin}
                          <button onClick={() => handleCreateRemoveOrigin(origin)}
                            className={`ml-1 hover:opacity-70 ${isDark ? "text-slate-400 hover:text-red-400" : "text-slate-500 hover:text-red-600"}`}
                            title="Entfernen">
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={createOriginInput}
                    onChange={e => setCreateOriginInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleCreateAddOrigin(); } }}
                    placeholder="z.B. example.com oder https://www.example.com"
                    className={isDark ? "bg-slate-950 border-slate-700 text-white" : "bg-white border-slate-300 text-slate-900"}
                  />
                  <Button type="button" onClick={handleCreateAddOrigin} disabled={!createOriginInput.trim()} className="bg-sky-600 hover:bg-sky-500 text-white">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={handleResetCreateDialog} disabled={creatingMunicipality}
                  className={isDark ? "border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"}>
                  Abbrechen
                </Button>
                <Button onClick={handleCreateMunicipality}
                  disabled={creatingMunicipality || !createForm.name.trim() || !createForm.slug.trim() || createForm.origins.length === 0}
                  className="bg-sky-600 hover:bg-sky-500 text-white">
                  {creatingMunicipality
                    ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Erstellen...</>)
                    : (<><Plus className="w-4 h-4 mr-2" />Gemeinde anlegen</>)}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
